<template>
  <v-row dense>
    <v-col cols="12">
      <!-- ⚠️ แท็บนี้บันทึกลงฐานข้อมูลจริง ไม่ใช่ localStorage เหมือนแท็บเก่า ๆ ในหน้านี้ -->
      <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-database-check-outline">
        <span class="text-body-2">
          {{ $t('qaSenDbNote') }}
        </span>
      </v-alert>

      <!-- สถานะจริง ไม่ใช่แค่ค่าสวิตช์ — เปิดสวิตช์ไว้แต่เลือกโหมด GPU ก็ยังไม่ทำงาน -->
      <v-alert
        v-if="!isActive"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-3"
        icon="mdi-pause-circle-outline"
      >
        <span class="text-body-2">
          {{ $t('qaSenOffLead') }}
          <template v-if="config.mode === 'gpu'">
            {{ $t('qaSenOffGpu') }}
          </template>
          <template v-else>
            {{ $t('qaSenOffSwitch') }}
          </template>
          {{ $t('qaSenOffTail', { endpoint: 'GET /api/panel/v1/sentiment' }) }}
        </span>
      </v-alert>
      <v-alert v-else type="success" variant="tonal" density="compact" class="mb-3" icon="mdi-play-circle-outline">
        <span class="text-body-2">
          {{ $t('qaSenOnBanner') }}
        </span>
      </v-alert>
    </v-col>

    <v-col cols="12" md="7">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaSenPromptTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <v-textarea
            v-model="config.systemPrompt"
            variant="outlined"
            rows="14"
            hide-details
            auto-grow
            spellcheck="false"
            :disabled="config.mode !== 'llm'"
            :placeholder="$t('qaEvalPromptPlaceholder')"
          />
          <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaSenPromptAppendNote') }}
            </span>
          </v-alert>
          <div class="text-caption text-medium-emphasis mt-2">
            {{ $t('qaEvalPromptNote') }}
          </div>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="5">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalOperation') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSenEnable') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSenEnableDesc') }}
              </div>
            </div>
            <v-switch v-model="config.enabled" color="primary" density="compact" hide-details inset />
          </div>

          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSenMode') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSenModeDesc') }}
              </div>
            </div>
            <v-btn-toggle v-model="config.mode" color="primary" density="compact" variant="outlined" divided mandatory>
              <v-btn value="llm" prepend-icon="mdi-brain">LLM</v-btn>
              <v-btn value="gpu" prepend-icon="mdi-expansion-card">GPU</v-btn>
            </v-btn-toggle>
          </div>

          <!-- ⚠️ เลือก GPU = ถือว่าปิดโหมด ไม่ใช่ถอยไปใช้ LLM แทน — ต้องบอกตรง ๆ
               ไม่งั้นคนตั้งค่าจะคิดว่าเปลี่ยนวิธีคำนวณ แต่ระบบยังทำงานอยู่ -->
          <v-alert
            v-if="config.mode === 'gpu'"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-2 mb-2"
          >
            <span class="text-body-2">
              {{ $t('qaSenGpuWarn') }}
            </span>
          </v-alert>

          <div v-if="config.mode === 'llm'" class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalModel') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaEvalModelDesc') }}
              </div>
            </div>
            <v-autocomplete
              v-model="config.modelId"
              :items="modelItems"
              item-title="title"
              item-value="value"
              :loading="modelsLoading"
              :error-messages="modelsError"
              density="compact"
              variant="outlined"
              hide-details="auto"
              clearable
              auto-select-first
              prepend-inner-icon="mdi-robot"
              :placeholder="$t('qaEvalModelDefault')"
              style="max-width: 320px"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
              </template>
            </v-autocomplete>
          </div>

          <!-- ⚠️ อยู่ในบล็อกของโหมด LLM โดยตั้งใจ — lead dev สั่งไว้ว่า "เงื่อนไขนี้ใช้เฉพาะโหมด LLM"
               โหมด GPU (ยังไม่ได้ทำ) จะมีจังหวะการทำงานของตัวเอง ไม่ผูกกับค่านี้ -->
          <div v-if="config.mode === 'llm'" class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSenEveryTurns') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSenEveryTurnsDesc') }}
              </div>
            </div>
            <div style="max-width: 320px">
              <v-text-field
                v-model.number="config.turnsPerRun"
                type="number"
                min="1"
                max="20"
                density="compact"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-repeat-variant"
                style="max-width: 120px"
              />
              <div class="text-caption text-medium-emphasis mt-1">{{ cadenceText }}</div>
            </div>
          </div>

          <!-- ⚠️ เพดานค่าใช้จ่ายเดียวที่ผูกกับ "1 บทสนทนา" — ดู QaSentimentSettings.MaxRunsPerCase -->
          <div v-if="config.mode === 'llm'" class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSenMaxRuns') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSenMaxRunsDesc') }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ $t('qaSenMaxRunsWarn') }}
              </div>
            </div>
            <div style="max-width: 320px">
              <v-text-field
                v-model.number="config.maxRunsPerCase"
                type="number"
                min="1"
                max="200"
                density="compact"
                variant="outlined"
                hide-details
                prepend-inner-icon="mdi-repeat-off"
                style="max-width: 120px"
              />
              <div class="text-caption text-medium-emphasis mt-1">{{ runsPerCaseText }}</div>
            </div>
          </div>

          <!-- ⚠️ ต้องมี v-if — ค่านี้คือ timeout ของการเรียก LLM · โชว์ค้างในโหมด GPU
               จะมีช่องชื่อ "Timeout" สองช่องบนจอเดียวกันที่คุมคนละอย่าง และคนตั้งค่า
               จะแก้ผิดช่องโดยไม่มีอะไรบอก -->
          <div v-if="config.mode === 'llm'" class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalTimeout') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaSenLlmTimeoutDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.timeoutSeconds" type="number" min="5" max="120" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>

          <!-- ══ โหมด GPU: ที่อยู่ของบริการวิเคราะห์อารมณ์จากเสียง ══════════════════
               ⚠️ ไม่มีช่องกรอก API Key ที่นี่ และห้ามเพิ่ม — ค่าตั้งก้อนนี้ถูกบันทึกลง
                  cc.QaSettings แบบ plain text และ endpoint ที่อ่านค่าเปิดให้ role "2" อ่านได้
                  คีย์อยู่ที่ env var ของ App Service เท่านั้น (ดู QaSentimentGpuSettings) -->
          <template v-if="config.mode === 'gpu'">
            <div class="qa-set-row qa-set-row--stack">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuEndpoint') }}</div>
                <div class="text-caption text-medium-emphasis">{{ $t('qaSenGpuEndpointDesc') }}</div>
              </div>
              <v-text-field
                v-model="config.gpu.endpoint"
                density="compact"
                variant="outlined"
                hide-details="auto"
                clearable
                spellcheck="false"
                :rules="endpointRules"
                placeholder="https://hostname"
                prepend-inner-icon="mdi-server-network"
              />
            </div>

            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuApiKey') }}</div>
                <div class="text-caption text-medium-emphasis">{{ $t('qaSenGpuApiKeyDesc') }}</div>
                <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaSenGpuApiKeyScope') }}</div>
              </div>
              <v-chip
                :color="gpuApiKeyConfigured ? 'success' : 'warning'"
                :prepend-icon="gpuApiKeyConfigured ? 'mdi-key' : 'mdi-key-alert-outline'"
                size="small"
                variant="tonal"
              >
                {{ gpuApiKeyConfigured ? $t('qaSenGpuApiKeySet') : $t('qaSenGpuApiKeyUnset') }}
              </v-chip>
            </div>

            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuTimeout') }}</div>
                <div class="text-caption text-medium-emphasis">{{ $t('qaSenGpuTimeoutDesc') }}</div>
              </div>
              <v-text-field v-model.number="config.gpu.timeoutSeconds" type="number" min="10" max="180" density="compact" variant="outlined" hide-details style="max-width: 100px" />
            </div>

            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuMaxClip') }}</div>
                <div class="text-caption text-medium-emphasis">{{ $t('qaSenGpuMaxClipDesc') }}</div>
              </div>
              <v-text-field v-model.number="config.gpu.maxClipSeconds" type="number" min="1" :max="15" density="compact" variant="outlined" hide-details style="max-width: 100px" />
            </div>

            <!-- ⚠️ อ่านอย่างเดียวโดยตั้งใจ — ค่านี้อยู่ที่แท็บเสียงและคุมการเขียน transcript ด้วย
                 มีช่องกรอกสองที่เมื่อไหร่ = สองค่าที่ขัดกันเองโดยไม่มีใครรู้ว่าตัวไหนจริง -->
            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuClipLen') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaSenGpuClipLenDesc', { sec: effectiveClipSeconds }) }}
                </div>
                <div v-if="clipCappedByService" class="text-caption text-warning mt-1">
                  {{ $t('qaSenGpuClipLenCapped', {
                    buffer: voiceBufferSeconds,
                    cap: effectiveClipSeconds,
                  }) }}
                </div>
              </div>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-timer-outline">
                {{ effectiveClipSeconds }}s
              </v-chip>
            </div>

            <div class="qa-set-row qa-set-row--stack">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaSenGpuTest') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaSenGpuTestDesc', { url: gpuHealthUrl || '{endpoint}/ready' }) }}
                </div>
                <div class="text-caption text-medium-emphasis mt-1">{{ $t('qaSenGpuTestLimits') }}</div>
              </div>
              <div>
                <v-btn
                  :loading="testing"
                  :disabled="!config.gpu.endpoint"
                  color="primary"
                  variant="tonal"
                  size="small"
                  prepend-icon="mdi-lan-connect"
                  @click="testGpu"
                >
                  {{ $t('qaSenGpuTest') }}
                </v-btn>

                <v-alert
                  v-if="testResult"
                  :type="testAlertType"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  <div class="text-body-2">{{ testResult.detail }}</div>
                  <div v-if="testResult.ready" class="text-caption mt-1">
                    {{ $t('qaSenGpuTestModel', { sha: testResult.ready.modelSha256 }) }}
                    <span v-if="testResult.ready.device"> · {{ testResult.ready.device }}</span>
                    <span v-if="testResult.ready.maxAudioSec"> · {{ $t('qaSenGpuTestMaxClip', { sec: testResult.ready.maxAudioSec }) }}</span>
                  </div>
                  <!-- บริการที่ไม่บังคับคีย์ = ใครก็เรียกได้ ซึ่งไม่ควรเป็นบน ingress สาธารณะ -->
                  <div v-if="testResult.ready && testResult.ready.authRequired === false" class="text-caption mt-1">
                    {{ $t('qaSenGpuTestNoAuth') }}
                  </div>
                  <div v-if="clipExceedsService" class="text-caption mt-1">
                    {{ $t('qaSenGpuTestClipTooLong', { set: config.gpu.maxClipSeconds, limit: testResult.ready.maxAudioSec }) }}
                  </div>
                </v-alert>
              </div>
            </div>
          </template>
        </div>
      </v-card>

      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaSenEscalateTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSenEscalateLabel') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSenEscalateDesc') }}
              </div>
            </div>
            <div style="max-width: 320px">
              <v-slider
                v-model="config.escalateBelow"
                :min="-1"
                :max="0"
                :step="0.05"
                color="error"
                density="compact"
                hide-details
                thumb-label
              />
              <div class="text-caption text-medium-emphasis">{{ $t('qaSenCurrentValue', { value: escalateText }) }}</div>
            </div>
          </div>
        </div>
      </v-card>

      <!-- ⚠️ ทั้งใบเป็นของโหมด LLM — จำนวนบรรทัด/ความยาวบรรทัดของ "บทสนทนาที่เป็นข้อความ"
           ไม่มีความหมายกับโหมดที่ส่งคลิปเสียง -->
      <v-card v-if="config.mode === 'llm'" class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalInputTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaNbaTailLines') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaNbaTailLinesDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.maxTranscriptLines" type="number" min="2" max="200" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalMaxLineLen') }}</div>
            </div>
            <v-text-field v-model.number="config.maxCharsPerLine" type="number" min="50" max="4000" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <v-alert type="info" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaSenPrivacyNote') }}
            </span>
          </v-alert>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaSetSaveConfig') }}</v-btn>
      <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
        {{ $t('qaSumLastEdited', { when: updatedAtText }) }}<template v-if="updatedBy"> {{ $t('qaSumLastEditedBy', { who: updatedBy }) }}</template>
      </span>
      <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSenNeverSaved') }}</span>
    </v-col>
  </v-row>
</template>

<script>
/**
 * แท็บตั้งค่า "อารมณ์ในบทสนทนา" (20 ส.ค. 2026)
 *
 * ⚠️ บันทึกลงฐานข้อมูลจริง (cc.QaSettings section 'sentiment') ไม่ใช่ localStorage
 *    facade (services/qaConsole/index.js) เหลือทางเดียวคือ API จริง — Q10 ลบชั้นจำลองทั้งชั้นแล้ว
 *    ห้ามเติมทางสำรองที่เขียนลง localStorage แล้วขึ้นว่าบันทึกสำเร็จ: ค่าที่คนตั้งจะไม่เคย
 *    ไปถึงงานเบื้องหลัง แต่หน้าจอรายงานว่าเรียบร้อย
 *
 * ⚠️ รายการโมเดลเป็น "บอทจากหน้า Model Creator" (endpoint เดิมของ framework
 *    `ManageGPTModel/DDGPTModel_IsPublishByUser` ตัวเดียวกับแท็บ Next best action)
 *    ห้ามสร้างรายการโมเดลชุดที่สองขึ้นมาเอง
 *
 * ⚠️ ค่าตั้งต้นของ enabled คือ **ปิด** ต่างจากแท็บ Next best action ที่เปิด —
 *    งานนี้ไม่มีด่านธรรมชาติกั้น เปิดทิ้งไว้ = ยิง LLM ทุกบทสนทนาทันทีที่ deploy
 */
import qaConsole from '@/services/qaConsole';

/**
 * ชื่อผลลัพธ์ของการทดสอบ — มาจากชั้น service ที่แปลง ordinal ให้แล้ว
 *
 * ⚠️ ห้ามเทียบกับชื่อสมาชิกของ enum ฝั่ง C# ตรง ๆ ('Ready') — ค่าที่วิ่งมาบนสายเป็น
 *    **ตัวเลข** เพราะ server ไม่ได้ลงทะเบียน JsonStringEnumConverter · การเทียบกับชื่อ
 *    จะไม่มีวันตรง แล้วทุกผลจะกลายเป็นสีแดงโดยไม่มี error ให้ใครเห็น
 */
const OUTCOME_READY = qaConsole.GPU_PROBE_OUTCOME.READY;
const OUTCOME_LOADING = qaConsole.GPU_PROBE_OUTCOME.LOADING;

/** ค่าตั้งต้นของก้อน gpu — ใช้ทั้งตอนตั้งค่าเริ่มต้นและตอนกู้ช่องตัวเลขที่ถูกลบจนว่าง */
const GPU_DEFAULTS = { timeoutSeconds: 60, maxClipSeconds: 15 };

/**
 * ล้าง URL ให้เหลือแค่ origin — **กติกาเดียวกับ QaSentimentGpuSettings.ToOrigin ฝั่ง server**
 *
 * ⚠️ มีสองฝั่งโดยตั้งใจ ไม่ใช่ความซ้ำซ้อนที่ลืมลบ:
 *    ฝั่ง server คือด่านจริง (ผู้เรียกอื่นข้ามหน้าจอได้) ส่วนฝั่งนี้ทำให้ค่าดิบ
 *    **ไม่ออกจากเบราว์เซอร์เลย** — request body ของ /api/v1/QaConsole/* ถูกเก็บลง
 *    telemetry ถ้าใครวาง URL ที่มีรหัสผ่านติดมา ค่านั้นจะไปนอนอยู่ในล็อกทั้งที่
 *    ค่าที่บันทึกลงฐานสะอาดแล้ว
 */
function toOrigin(raw) {
  if (!raw || !String(raw).trim()) return '';
  let text = String(raw).trim();
  if (!text.includes('://')) text = `https://${text}`;

  try {
    const url = new URL(text);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
    if (!url.hostname) return '';
    // http ได้เฉพาะเครื่องตัวเอง — คีย์เดินทางไปกับ header ทุกครั้งที่เรียกบริการจริง
    const loopback = ['localhost', '127.0.0.1', '[::1]', '::1'].includes(url.hostname);
    if (url.protocol === 'http:' && !loopback) return '';
    return url.origin;
  } catch {
    return '';
  }
}

/**
 * ตัวเลือกตั้งต้น — ปล่อยให้ระบบใช้โมเดลของ OA ที่เคสนั้นสังกัด
 * (พฤติกรรมเดิมของ social pipeline ทั้งเส้น)
 */

export default {
  name: 'QaSentimentSettings',
  emits: ['saved'],
  data() {
    return {
      config: {
        enabled: false,
        mode: 'llm',
        modelId: null,
        turnsPerRun: 2,
        maxRunsPerCase: 20,
        systemPrompt: '',
        timeoutSeconds: 20,
        maxTranscriptLines: 40,
        maxCharsPerLine: 500,
        escalateBelow: -0.5,
        // ⚠️ ไม่มี apiKey ในก้อนนี้ และห้ามเพิ่ม — ก้อนนี้ถูก POST ขึ้นไปแล้วบันทึกลง
        //    cc.QaSettings แบบอ่านได้ · คีย์อยู่ที่ env var ของ App Service เท่านั้น
        gpu: { endpoint: '', timeoutSeconds: 60, maxClipSeconds: 15 },
      },
      isActive: false,
      // ⚠️ server ส่งมาแค่ "มี/ไม่มี" ไม่เคยส่งค่าคีย์ — หน้าจอห้ามมีตัวแปรที่ถือคีย์
      gpuApiKeyConfigured: false,
      // ⚠️ มาจาก section "voice-stream" คนละแถวกับหน้านี้ — โชว์เฉย ๆ ไม่ได้บันทึกกลับ
      voiceBufferSeconds: null,
      testing: false,
      testResult: null,
      updatedAt: null,
      updatedBy: null,
      saving: false,
      modelsLoading: false,
      modelsError: '',
      modelItems: [],
    };
  },
  computed: {
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
    cadenceText() {
      const n = Number(this.config.turnsPerRun);
      if (!Number.isFinite(n) || n < 1) return '';
      if (n === 1) return this.$t('qaSenTurn1');
      if (n === 2) return this.$t('qaSenTurn2');
      return this.$t('qaSenTurnN', { n, rounds: Math.round(n / 2) });
    },
    /**
     * แปลงเพดานเป็นหน่วยที่คนตั้งค่าเห็นภาพ — "กี่ครั้ง" ไม่มีความหมายจนกว่าจะรู้ว่า
     * เท่ากับบทสนทนายาวแค่ไหน · ตัวเลขนี้เปลี่ยนตาม turnsPerRun ที่ตั้งไว้จริง
     */
    runsPerCaseText() {
      const runs = Number(this.config.maxRunsPerCase);
      if (!Number.isFinite(runs) || runs < 1) return '';
      const turns = Math.max(1, Number(this.config.turnsPerRun) || 1);
      return this.$t('qaSenCapText', { runs, turns: runs * turns });
    },
    escalateText() {
      const v = Number(this.config.escalateBelow);
      if (Number.isNaN(v)) return '—';
      return `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)}`;
    },
    /** URL ตรวจสุขภาพที่จะยิงจริง — คำนวณจากค่าที่พิมพ์อยู่ ไม่ใช่ค่าที่บันทึกแล้ว */
    gpuHealthUrl() {
      const origin = toOrigin(this.config.gpu.endpoint);
      return origin ? `${origin}/ready` : '';
    },
    endpointRules() {
      // ⚠️ ต้องมีกฎฝั่งหน้าจอ ไม่ใช่ปล่อยให้ server ล้างอย่างเดียว — ค่าที่ล้างไม่ผ่านจะกลาย
      //    เป็นสตริงว่าง แล้วหน้าจอจะขึ้นว่า "บันทึกสำเร็จ" พร้อมช่องที่ว่างเปล่า
      //    โดยคนกรอกไม่รู้ว่าเพราะอะไร
      return [(v) => !v || !!toOrigin(v) || this.$t('qaSenGpuEndpointInvalid')];
    },
    testAlertType() {
      if (!this.testResult) return 'info';
      if (this.testResult.outcome === OUTCOME_READY) return 'success';
      if (this.testResult.outcome === OUTCOME_LOADING) return 'warning';
      return 'error';
    },
    /**
     * ตั้งความยาวคลิปไว้เกินกว่าที่บริการ**ตัวที่รันอยู่จริง**รับ — คลิปทุกใบจะโดน 413
     * โดยหน้าจอไม่มีอะไรผิดปกติให้เห็น · รู้ได้ต่อเมื่อกดทดสอบแล้วเท่านั้น
     */
    /**
     * ความยาวคลิปที่จะถูกส่งจริง = min(รอบ buffer จากแท็บเสียง, เพดานความยาวคลิป)
     *
     * ⚠️ สูตรเดียวกับ VoiceStreamCoordinator.BuildEmotionPlan ฝั่ง middleware —
     *    ถ้าสองที่นี้ไม่ตรงกัน หน้าจอจะบอกเลขหนึ่งแต่ระบบส่งอีกเลขหนึ่ง
     */
    effectiveClipSeconds() {
      const buffer = Number(this.voiceBufferSeconds);
      const cap = Number(this.config.gpu.maxClipSeconds) || 15;
      if (!buffer) return cap;
      return Math.max(1, Math.min(buffer, cap));
    },

    /** รอบ buffer ยาวกว่าที่บริการรับได้ — คลิปจะถูกตัดสั้นลง แต่ transcript ยังใช้รอบเต็ม */
    clipCappedByService() {
      const buffer = Number(this.voiceBufferSeconds);
      return !!buffer && buffer > this.effectiveClipSeconds;
    },

    clipExceedsService() {
      const limit = this.testResult && this.testResult.ready && this.testResult.ready.maxAudioSec;
      return !!limit && Number(this.config.gpu.maxClipSeconds) > Number(limit);
    },
  },
  mounted() {
    this.load();
    this.loadModels();
    this.loadVoiceBuffer();
  },
  methods: {
    /**
     * ดึงรอบ buffer จากแท็บเสียงมาแสดง
     *
     * ⚠️ ล้มแล้วเงียบโดยตั้งใจ — เป็นข้อมูลประกอบ ไม่ใช่ค่าที่หน้านี้บันทึก
     *    ขึ้น error ให้คนตั้งค่าเห็นจะชวนให้คิดว่าบันทึกหน้านี้ไม่ได้
     */
    async loadVoiceBuffer() {
      try {
        const res = await qaConsole.getVoiceStreamConfig();
        const value = res && res.value ? Number(res.value.bufferSeconds) : 0;
        if (value > 0) this.voiceBufferSeconds = value;
      } catch {
        this.voiceBufferSeconds = null;
      }
    },

    /** หัวรายการ dropdown เลือกโมเดล — เมธอด ไม่ใช่ const ระดับโมดูล (ดู QaEvaluatorSettings) */
    defaultModelItem() {
      return { title: this.$t('qaEvalModelDefaultTitle'), subtitle: this.$t('qaEvalModelDefaultSub'), value: null };
    },
    async load() {
      try {
        const res = await qaConsole.getSentimentSettings();
        if (res && res.value) this.config = { ...this.config, ...res.value };
        // ⚠️ isActive มาจาก server ไม่ได้คำนวณเอง — กติกา "โหมด GPU = ปิด" ต้องมีนิยามเดียว
        //    ทั้งระบบ ไม่งั้นหน้าจอกับ worker จะเชื่อคนละอย่างเมื่อกติกาเปลี่ยน
        this.isActive = !!(res && res.isActive);
        // ⚠️ bool ล้วน ไม่มีค่าคีย์ให้ถือ — ดู QaSentimentSettingsResult.GpuApiKeyConfigured
        this.gpuApiKeyConfigured = !!(res && res.gpuApiKeyConfigured);
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaEvalLoadFailed', { msg: err.message }) });
      }
    },

    /**
     * ทดสอบว่าบริการรับสายอยู่ไหม — ส่ง endpoint ที่พิมพ์ไว้ไปด้วย เพื่อให้ทดสอบก่อนบันทึกได้
     *
     * ⚠️ ไม่ส่งคีย์ และไม่มีคีย์ให้ส่ง — server หยิบจาก configuration ของตัวเอง
     */
    async testGpu() {
      this.testing = true;
      this.testResult = null;
      try {
        this.testResult = await qaConsole.testSentimentGpu(toOrigin(this.config.gpu.endpoint));
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSenGpuTestFailed', { msg: err.message }) });
      } finally {
        this.testing = false;
      }
    },

    /**
     * รายการบอทจากหน้า Model Creator — endpoint เดียวกับแท็บ Next best action
     * (`api/v0/ManageGPTModel/DDGPTModel_IsPublishByUser` · กรอง ActiveFlag + IsPublish
     *  และ scope ตาม role/company ให้แล้วฝั่ง server)
     *
     * ⚠️ payload ใช้ casing ผสม — r.id / r.name เป็นตัวเล็ก แต่ r.TypeName เป็น PascalCase
     *    (ResponseDropDownModel) · เขียนผิด casing จะได้ subtitle ว่างเงียบ ๆ
     */
    loadModels() {
      const self = this;
      if (!this.$call || typeof this.$call.DDGPTModel_IsPublishByUser !== 'function') return;

      this.modelsLoading = true;
      this.$call.DDGPTModel_IsPublishByUser(
        (dataResponse) => {
          self.modelsLoading = false;
          if (!dataResponse || dataResponse.status !== 0) {
            self.modelsError = self.$t('qaEvalModelsFailed');
            return;
          }
          const rows = dataResponse.data || [];
          self.modelsError = rows.length ? '' : self.$t('qaEvalNoModels');
          self.modelItems = [self.defaultModelItem()].concat(
            rows.map((r) => ({
              title: r.name || `#${r.id}`,
              subtitle: [r.TypeName, `ModelID ${r.id}`].filter(Boolean).join(' · '),
              value: r.id,
            }))
          );
        },
        () => {
          self.modelsLoading = false;
          // ดึงรายการไม่ได้ก็ยังตั้งค่าอื่นได้ — เหลือตัวเลือกตั้งต้นไว้ตัวเดียว
          self.modelsError = self.$t('qaEvalModelsFailed');
        }
      );
    },

    async save() {
      this.saving = true;
      try {
        // ⚠️ ปั้น payload ใหม่ ไม่ส่ง this.config ดิบ ๆ ด้วยสองเหตุผล:
        //    1. ช่อง v-model.number ที่ถูกลบจนว่างจะเป็นสตริง '' ซึ่ง model binder ฝั่ง server
        //       ปฏิเสธเป็น 400 — คนตั้งค่าจะเห็นแค่ "บันทึกไม่สำเร็จ" แล้วค่าที่เพิ่งพิมพ์หายไป
        //    2. endpoint ดิบอาจมี userinfo/query ติดมา · ล้างก่อนออกจากเบราว์เซอร์
        //       ค่านั้นจะได้ไม่ไปนอนอยู่ใน telemetry ที่เก็บ request body
        const payload = {
          ...this.config,
          gpu: {
            endpoint: toOrigin(this.config.gpu.endpoint),
            timeoutSeconds: Number(this.config.gpu.timeoutSeconds) || GPU_DEFAULTS.timeoutSeconds,
            maxClipSeconds: Number(this.config.gpu.maxClipSeconds) || GPU_DEFAULTS.maxClipSeconds,
          },
        };

        const res = await qaConsole.saveSentimentSettings(payload);
        // ⚠️ ใช้ค่าที่ server คืนกลับมา ไม่ใช่ค่าที่พิมพ์ — server บีบค่าที่เกินขอบให้อยู่ในช่วง
        //    ที่ใช้ได้ (และเปลี่ยน modelId <= 0 เป็น null)
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.isActive = !!(res && res.isActive);
        this.gpuApiKeyConfigured = !!(res && res.gpuApiKeyConfigured);
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaSenSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ⚠️ คัดลอกมาจาก QaNextBestActionSettings.vue โดยตั้งใจ — คลาสนี้เป็น scoped style
   ของแต่ละ component ไม่ใช่ของกลางใน qa-console.css ถ้าไม่ใส่ แถวตั้งค่าจะกองซ้อนกันเป็นแนวตั้ง */
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
/* แถวที่คำอธิบายยาว — วางช่องเลือกไว้ใต้คำอธิบายแทนที่จะบีบให้อยู่ข้าง ๆ */
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
</style>
